import React, { useEffect } from "react";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import WhatsAppButton from "@/components/WhatsAppButton";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import Build from "@/pages/Build";
import Plans from "@/pages/Plans";
import PlanDetail from "@/pages/PlanDetail";
import Pricing from "@/pages/Pricing";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import DesignCheckout from "@/pages/DesignCheckout";
import OrderSuccess from "@/pages/OrderSuccess";
import Customize from "@/pages/Customize";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PartnerRegister from "@/pages/PartnerRegister";
import Dashboard from "@/pages/Dashboard";
import AddEditPlanPage from "@/pages/dashboards/AddEditPlanPage";
import { BlogList, BlogPost } from "@/pages/Blog";
import { CareerList, CareerDetail } from "@/pages/Career";
import { About, Services, Terms, Privacy, Refund, Cancellation, Delivery, Legal, NotFound } from "@/pages/Static";
import { AffiliateLanding } from "@/pages/Affiliate";
import AffiliateTracker from "@/components/AffiliateTracker";
import { Toaster } from "@/components/ui/sonner";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
}

function Layout({ children }) {
  const loc = useLocation();
  const hideChrome = loc.pathname.startsWith("/login") || loc.pathname.startsWith("/signup") || loc.pathname.startsWith("/partner/register") || loc.pathname.startsWith("/forgot-password") || loc.pathname.startsWith("/reset-password");
  return (
    <>
      <Header />
      <main>{children}</main>
      {!hideChrome && <WhatsAppButton />}
      <StickyMobileCTA />
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
    <I18nProvider>
      <AuthProvider>
        <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Layout>
            <AffiliateTracker />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/build" element={<Build />} />
              <Route path="/cost-calculator" element={<Pricing />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/plans/:slug" element={<PlanDetail />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/design-checkout/:tier" element={<DesignCheckout />} />
              <Route path="/order/success" element={<OrderSuccess />} />
              <Route path="/customize" element={<Customize />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/cancellation" element={<Cancellation />} />
              <Route path="/delivery" element={<Delivery />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/affiliate" element={<AffiliateLanding />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/careers" element={<CareerList />} />
              <Route path="/careers/:slug" element={<CareerDetail />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin" element={<ProtectedRoute roles={["admin", "superadmin"]}><Dashboard /></ProtectedRoute>} />
              <Route path="/admin/plans/new" element={<ProtectedRoute roles={["admin", "superadmin"]}><AddEditPlanPage /></ProtectedRoute>} />
              <Route path="/admin/plans/:planId/edit" element={<ProtectedRoute roles={["admin", "superadmin"]}><AddEditPlanPage /></ProtectedRoute>} />
              <Route path="/superadmin" element={<ProtectedRoute roles={["superadmin"]}><Dashboard /></ProtectedRoute>} />
              <Route path="/partner" element={<ProtectedRoute roles={["partner"]}><Dashboard /></ProtectedRoute>} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/partner/register" element={<PartnerRegister />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
          <Toaster />
        </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
    </HelmetProvider>
  );
}
